import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Loader2,
  MessageSquareText,
  Slack,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { User } from '../../types';
import { useSlackConnection, useUpdateSlackConnection } from '../../hooks';
import { SlackSyncCard } from './SlackSyncCard';
import { TestNotificationsCard } from './TestNotificationsCard';
import { AdminSettingCard } from './AdminSettingCard';

/** Channel fields editable from this card, in display order. */
const CHANNEL_FIELDS = ['kudos', 'birthday', 'anniversary', 'invoice'] as const;

type ChannelField = (typeof CHANNEL_FIELDS)[number];

type ChannelDraft = Record<ChannelField, string>;

const EMPTY_DRAFT: ChannelDraft = {
  kudos: '',
  birthday: '',
  anniversary: '',
  invoice: '',
};

const NOTIFICATION_TEMPLATES_PATH = '/settings/notification-templates';

interface SlackChannelsCardProps {
  /** Current admin, used to send the test notifications. */
  user: User;
}

/**
 * Single collapsible "Slack" card aggregating the organization's Slack
 * settings: user/emoji sync, the notification channels (loaded from and saved
 * to the Slack connection), test notifications and links to the Slack pages.
 */
export const SlackChannelsCard: React.FC<SlackChannelsCardProps> = ({ user }) => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ChannelDraft>(EMPTY_DRAFT);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: connection, isLoading, isError } = useSlackConnection();
  const updateConnection = useUpdateSlackConnection();

  useEffect(() => {
    if (!connection) {
      return;
    }
    setDraft({
      kudos: connection.kudosChannel ?? '',
      birthday: connection.birthdayChannel ?? '',
      anniversary: connection.anniversaryChannel ?? '',
      invoice: connection.invoiceChannel ?? '',
    });
  }, [connection]);

  const handleChannelChange = (field: ChannelField, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaved(false);
    setSaveError(null);
    try {
      await updateConnection.mutateAsync({
        kudosChannel: draft.kudos.trim(),
        birthdayChannel: draft.birthday.trim(),
        anniversaryChannel: draft.anniversary.trim(),
        invoiceChannel: draft.invoice.trim(),
      });
      setSaved(true);
    } catch (error) {
      console.error('Failed to save Slack channels:', error);
      setSaveError(t('slackChannels.error'));
    }
  };

  return (
    <AdminSettingCard
      icon={Slack}
      title={t('slackChannels.cardTitle')}
      description={t('slackChannels.cardDescription')}
      data-testid="slack-channels-card"
      headerTestId="slack-channels-collapse-toggle"
    >
      <SlackSyncCard />

      <div className="border rounded-lg p-4 space-y-4" data-testid="slack-channels-section">
        <div>
          <h3 className="font-semibold text-lg mb-1">{t('slackChannels.channelsTitle')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('slackChannels.channelsDescription')}
          </p>
        </div>

        {isLoading ? (
          <div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="slack-channels-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('slackChannels.loading')}
          </div>
        ) : isError ? (
          <Alert variant="destructive" data-testid="slack-channels-load-error-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t('slackChannels.loadError')}</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {CHANNEL_FIELDS.map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={`slack-channel-${field}`}>
                    {t(`slackChannels.labels.${field}`)}
                  </Label>
                  <Input
                    id={`slack-channel-${field}`}
                    value={draft[field]}
                    onChange={(event) => handleChannelChange(field, event.target.value)}
                    placeholder={t(`slackChannels.placeholders.${field}`)}
                    data-testid={`slack-channels-input-${field}`}
                  />
                </div>
              ))}
            </div>

            {saved && (
              <Alert data-testid="slack-channels-success-alert">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{t('slackChannels.success')}</AlertDescription>
              </Alert>
            )}

            {saveError && (
              <Alert variant="destructive" data-testid="slack-channels-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleSave}
              disabled={updateConnection.isPending}
              className="gap-2"
              data-testid="slack-channels-save-button"
            >
              {updateConnection.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('slackChannels.saving')}
                </>
              ) : (
                t('slackChannels.save')
              )}
            </Button>
          </>
        )}
      </div>

      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted/50"
        onClick={() => navigate(NOTIFICATION_TEMPLATES_PATH)}
        data-testid="slack-channels-link-notification-templates"
      >
        <div className="flex items-center gap-3">
          <MessageSquareText className="h-5 w-5 shrink-0 text-primary" />
          <div>
            <div className="font-semibold">{t('notificationTemplates.cardTitle')}</div>
            <p className="text-sm text-muted-foreground">
              {t('notificationTemplates.cardDescription')}
            </p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </button>

      <TestNotificationsCard user={user} />
    </AdminSettingCard>
  );
};
